/** @jsxImportSource @emotion/react */
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from '@emotion/styled';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Grid,
  Link,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { setPrice, setStep } from '../santeMobiliteSlice';
import {
  downloadSanteMobiliteAttestation,
  fetchSanteMobilitePrice,
} from '../santeMobiliteThunk';
import { selectInclusionPath } from '../selectors';
import { computeMonthsCount } from '../utils';
import { getSchoolByStudent } from '@/redux/actions/schoolsActions';
import { downloadGuaranteesTable } from '@/redux/actions/InsuranceActions';
import { Roles } from '@/common/Roles';
import { formatDateOnly } from '@/utils/date';
import HealthMobilityWarrantyAccordion from '../components/HealthMobilityWarrantyAccordion';
import EditableRecapCard from '../components/EditableRecapCard';
import SanteMobilitePaymentForm from '../components/SanteMobilitePaymentForm';

type SchoolWithPartnerLevel = { partnerLevelHealthMobility?: number } | undefined;
type FetchPricePayload = { stripe?: { unit_amount?: number } };

const ButtonStyled = styled(Button)`
  background-color: #2f6975;
  color: white;
  border-radius: 30px;
  padding: 8px 24px;
  font-weight: 500;
  &:hover {
    background-color: #26555f;
  }
`;

// computeMonthsCount centralisé dans ../utils.ts — voir les commentaires du
// fichier pour la règle métier exacte et pourquoi on ne la duplique plus.

const FinalizationStep = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { t } = useTranslation('sante-mobilite');

  const { startDate, endDate, destination, price, hasFrenchSocialSecurity } = useAppSelector(
    state => state.santeMobilite,
  );
  const inclusionPath = useAppSelector(selectInclusionPath);
  const school = useAppSelector(state => state.school?.school);
  const currentUser = useAppSelector(state => state.auth?.data?.user);

  const [priceLoading, setPriceLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  // L'attestation a-t-elle DÉJÀ été obtenue pendant cette visite du step 5 ?
  // `POST /contracts/health_mobility` n'est pas idempotent : chaque appel crée
  // un Contract, un Document et envoie un email. Un utilisateur qui reclique
  // pour « re-télécharger » — le libellé du bouton l'y invite — se retrouvait
  // avec des doublons dans son espace et autant d'emails. Vérifié en preprod :
  // 3 clics = 3 contrats.
  const [attestationDone, setAttestationDone] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);

  // Charge l'école de l'étudiant si on ne l'a pas encore (besoin pour calculer
  // inclusionPath correctement). Endpoint réservé aux comptes rattachés à une
  // école (inclusion / partner) : on ne l'appelle pas pour un classic, ça
  // déclencherait un 401 sans valeur ajoutée (le back rejette les comptes non
  // vérifiés ou sans école sur /schools/my, ce qui mènerait à un logout via
  // l'interceptor axios).
  useEffect(() => {
    const roles: string[] = currentUser?.roles || [];
    const hasSchoolAttached =
      roles.includes(Roles.STUDENT_INCLUSIVE.value) ||
      roles.includes(Roles.STUDENT_PARTNER.value);
    if (!hasSchoolAttached) return;
    if (!school || Object.keys(school).length === 0) {
      dispatch(getSchoolByStudent() as unknown as ReturnType<typeof dispatch>);
    }
  }, [dispatch, school, currentUser]);

  // Charge le prix uniquement si on est en parcours payant et qu'on a tout ce qu'il faut.
  useEffect(() => {
    if (inclusionPath !== 'paid') return;
    if (!destination?.zone || !startDate || !endDate) return;
    if (price !== null) return;

    let cancelled = false;
    setPriceLoading(true);
    const zone = destination.zone;
    (async () => {
      const partnerLevel =
        (school as SchoolWithPartnerLevel)?.partnerLevelHealthMobility ?? 0;
      const monthsCount = computeMonthsCount(startDate, endDate);
      const res = await dispatch(
        fetchSanteMobilitePrice({
          zone,
          monthsCount,
          partnerLevelHealthMobilityPrice: partnerLevel,
          hasFrenchSocialSecurity,
        }),
      );
      if (cancelled) return;
      if (fetchSanteMobilitePrice.fulfilled.match(res)) {
        const stripeAmount =
          (res.payload as FetchPricePayload)?.stripe?.unit_amount ?? null;
        if (stripeAmount != null) {
          dispatch(setPrice(stripeAmount / 100));
        }
      } else if (fetchSanteMobilitePrice.rejected.match(res)) {
        // Cas zone 0 (pays non couvert) : on renvoie l'utilisateur vers la step
        // Destination pour qu'il change de pays. La sélection actuelle reste
        // affichée avec l'alert "pays non couvert".
        const payload = res.payload as { countryNotCovered?: boolean } | undefined;
        if (payload?.countryNotCovered) {
          dispatch(setStep(2));
        }
      }
      setPriceLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [dispatch, inclusionPath, destination, startDate, endDate, price, school, hasFrenchSocialSecurity]);

  const handleDownloadAttestation = async () => {
    if (!startDate || !endDate || !destination) return;
    // Garde-fou : ni appel concurrent (double-clic, réseau lent), ni second
    // appel après un succès. En cas d'échec on laisse réessayer, sinon un
    // incident réseau bloquerait l'étudiant sans recours.
    if (downloading || attestationDone) return;
    setDownloading(true);
    const res = await dispatch(
      downloadSanteMobiliteAttestation({
        startDate: formatDateOnly(startDate),
        endDate: formatDateOnly(endDate),
        country: destination,
      }),
    );
    setDownloading(false);
    // Succès → on redirige vers la page de fin de parcours pour clore l'UX,
    // au lieu de laisser l'utilisateur perplexe sur l'étape 5 avec juste un
    // toastr. Le flag `?free=1` indique à la SuccessPage qu'on vient du
    // parcours gratuit (pas de contractReference Stripe, pas de SSE) — elle
    // affiche un message merci générique + propose de re-télécharger.
    if (downloadSanteMobiliteAttestation.fulfilled.match(res)) {
      setAttestationDone(true);
      // On transmet la référence du contrat qui vient d'être créé, exactement
      // comme le parcours payant transmet la sienne au retour de Stripe.
      // L'écran de remerciement s'en sert pour RELIRE l'attestation par son
      // identifiant. Sans elle, il n'avait que les dates et le pays, et son
      // bouton « re-télécharger » recréait un contrat à chaque clic.
      const { attestationDocumentId, contractReference } =
        (res.payload as {
          attestationDocumentId: number | null;
          contractReference: string | null;
        }) || {};
      const params = new URLSearchParams({ free: '1' });
      if (attestationDocumentId) {
        params.set('attestationId', String(attestationDocumentId));
      }
      if (contractReference) params.set('contractReference', contractReference);
      history.push(`/insurance/health-mobility/success?${params.toString()}`);
    }
  };

  const handleClickPayment = () => {
    if (!destination || !startDate || !endDate) return;
    setPaymentModalOpen(true);
  };

  const recapCard = <EditableRecapCard />;

  /**
   * Aiguillage du téléchargement du tableau de garantie :
   *  - inclusion : appel back qui renvoie le PDF négocié avec l'école.
   *  - tous les autres (classic, partner, non-connecté) : PDF générique statique
   *    servi depuis /public/document/, pas d'appel back. Deux variantes selon
   *    la couverture effective :
   *      - tableau_garanties_sante_mobilite_zone4.pdf : zone 4 (couverture
   *        "étranger" appliquée par le back) — soit le pays choisi est en
   *        zone 4, soit le user n'a pas de num de sécu FR (auquel cas le back
   *        force la zone 4 peu importe la destination).
   *      - tableau_garanties_sante_mobilite.pdf : toutes les autres zones.
   */
  const handleDownloadGuaranteesTable = () => {
    const roles: string[] = currentUser?.roles || [];
    const isInclusion = roles.includes(Roles.STUDENT_INCLUSIVE.value);
    if (isInclusion) {
      dispatch(
        downloadGuaranteesTable() as unknown as ReturnType<typeof dispatch>,
      );
      return;
    }
    const zoneNum =
      typeof destination?.zone === 'string'
        ? parseInt(destination.zone, 10)
        : destination?.zone;
    // hasFrenchSocialSecurity === false suffit à forcer zone 4 côté back. On
    // reproduit la même règle côté front pour rester cohérent avec la
    // couverture effective (et donc le tableau de garantie correct).
    const isZone4 = zoneNum === 4 || hasFrenchSocialSecurity === false;
    const file = isZone4
      ? 'tableau_garanties_sante_mobilite_zone4.pdf'
      : 'tableau_garanties_sante_mobilite.pdf';
    const link = document.createElement('a');
    link.href = `/document/${file}`;
    link.download = file;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const guaranteesTableButton = (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
      <Button
        onClick={handleDownloadGuaranteesTable}
        variant="outlined"
        startIcon={<DescriptionOutlinedIcon />}
        sx={{
          borderRadius: 30,
          textTransform: 'none',
          borderColor: '#2f6975',
          color: '#2f6975',
        }}
      >
        {t('step5.download_guarantees_table')}
      </Button>
    </Box>
  );

  /**
   * Documents contractuels obligatoires (IPID + conditions générales).
   * Fichiers statiques servis depuis /public/document/health_mobility/, comme
   * le tableau de garantie ci-dessus. Un seul assureur (AXA) aujourd'hui, d'où
   * les noms en dur ; le libellé affiché masque le suffixe assureur. Si un
   * assureur non-AXA est ajouté, basculer ces sources sur price.ipid/price.cg
   * renvoyés par le back (comme le faisait l'ancien parcours).
   */
  const contractDocuments = [
    { label: 'ipid.pdf', file: 'ipid_axa.pdf' },
    { label: 'cg.pdf', file: 'cg_axa.pdf' },
  ];

  const contractDocumentsLinks = (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        gap: 3,
        mt: 1.5,
        flexWrap: 'wrap',
      }}
    >
      {contractDocuments.map(doc => (
        <Link
          key={doc.file}
          href={`/document/health_mobility/${doc.file}`}
          download={doc.label}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            color: '#2f6975',
            fontSize: 14,
            textDecoration: 'underline',
            '&:hover': { color: '#26555f' },
          }}
        >
          <DescriptionOutlinedIcon sx={{ fontSize: 16 }} />
          {doc.label}
        </Link>
      ))}
    </Box>
  );

  if (inclusionPath === 'free') {
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto', p: 2 }}>
        <Typography variant="subtitle1" gutterBottom mb={0}>
          {t('step5.free_title')}
        </Typography>
        <Alert severity="success" sx={{ mt: 2 }}>
          {t('step5.free_eligible_message')}
        </Alert>

        <Box mt={3}>{recapCard}</Box>

        {contractDocumentsLinks}

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <ButtonStyled
            onClick={handleDownloadAttestation}
            disabled={downloading || attestationDone}
            startIcon={
              downloading ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />
            }
          >
            {t('step5.download_attestation')}
          </ButtonStyled>
        </Box>

        {guaranteesTableButton}
        <HealthMobilityWarrantyAccordion />
      </Box>
    );
  }

  // Parcours payant
  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: 2 }}>
      <Typography variant="subtitle1" gutterBottom mb={0}>
        {t('step5.paid_title')}
      </Typography>

      <Grid container spacing={2} mt={1}>
        <Grid item xs={12} sm={7}>
          {recapCard}
          {contractDocumentsLinks}
          {guaranteesTableButton}
        </Grid>
        <Grid item xs={0} sm={1} />
        <Grid item xs={12} sm={4}>
          <Box sx={{ textAlign: 'center' }}>
            {priceLoading ? (
              <CircularProgress size={24} />
            ) : price !== null ? (
              <Typography fontSize={{ xs: '28px', sm: '36px' }} fontWeight={400}>
                {price.toFixed(2).replace('.', ',')}€
              </Typography>
            ) : null}
            <ButtonStyled
              disabled={!startDate || !endDate || price === null || priceLoading}
              onClick={handleClickPayment}
              sx={{ mt: 2 }}
            >
              {t('step5.subscribe')}
            </ButtonStyled>
          </Box>
        </Grid>
      </Grid>

      <HealthMobilityWarrantyAccordion />

      {/* Modal Stripe Elements pour le paiement (calque sur le pattern RC). */}
      <Dialog
        open={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent>
          {startDate && endDate && destination && (
            <SanteMobilitePaymentForm
              startDate={startDate}
              endDate={endDate}
              country={destination}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default FinalizationStep;
